import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Button,
  Badge,
  Modal,
  Spinner,
  InputGroup,
  Form,
  Tabs,
  Tab,
  Card,
  Col,
  Table,
  Alert,
  Row,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { CONFIG } from "../utils/config";

/**
 * ============================================================================
 * HistoryManager Component
 * ============================================================================
 * * PURPOSE:
 * 1. Lists all SMS transactions (Single, Bulk, Group) from the local database.
 * 2. Allows filtering and searching of logs by Mobile, Template ID, or Type.
 * 3. Provides detailed tracking via "View" and "Track" buttons using BSNL APIs.
 * 4. Implements BSNL Cancellation Logic with immediate UI updates.
 * 5. 🔥 NEW: Batch Stats & Bulk Cancellation Logic.
 * * API INTEGRATION:
 * - Fetches history from: GET /api/db/history
 * - Cancels via: POST /api/Bsnl/cancel-schedule
 * - Tracks via: POST /api/Bsnl/check-status
 * * AUTHOR: Amar Ranjan (Nanoplex)
 * ============================================================================
 */
function HistoryManager({ addLog, initialFilter, notify }) {

  // ============================================================================
  // 1. STATE MANAGEMENT
  // ============================================================================

  // Main Data State
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  // Modal & Selection State
  const [selectedLog, setSelectedLog] = useState(null);
  const [fetchedBatchDetails, setFetchedBatchDetails] = useState([]);

  // Live Tracking State
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerData, setTrackerData] = useState(null);

  // 🔥 NEW: State for Cancellation Audit Log
  const [cancelAuditLog, setCancelAuditLog] = useState(null);

  // Cancellation Action State
  const [cancellingIds, setCancellingIds] = useState([]);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [batchCache, setBatchCache] = useState({});


  // ============================================================================
  // 2. CONFIGURATION & CONSTANTS
  // ============================================================================

  // BSNL Rule: Users can cancel a scheduled SMS "half an hour" before scheduled time.
  // We use a safe buffer to prevent API rejection.
  const CANCELLATION_BUFFER_MINUTES = 30;

  // ============================================================================
  // 3. DATA LOADING & INITIALIZATION
  // ============================================================================

  /**
   * Fetches the full transaction history from the SQLite database.
   * API: GET {CONFIG.DB_BASE}/history
   */
  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch latest data from backend
      const res = await axios.get(`${CONFIG.DB_BASE}/history`);

      if (res.data) {
        // Sort by timestamp descending (Newest First)
        const sorted = res.data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setLogs(sorted);
      }
    } catch (e) {
      console.error("History Load Error:", e);
      if (addLog) addLog("Failed to load history: " + e.message, "ERROR");
    } finally {
      setLoading(false);
    }
  }, [addLog]);

  // Trigger load on component mount
  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // ============================================================================
  // 4. FILTERING & SEARCH LOGIC
  // ============================================================================

  const globalFiltered = logs.filter(l => {
    // A. Filter by Tab/Status passed from Dashboard (if any)
    if (initialFilter && initialFilter !== "All") {
      if (initialFilter === "Failed" && l.status === "Success") return false;
      if (initialFilter !== "Failed" && l.status !== initialFilter) return false;
    }

    // B. Filter by Search Input
    if (!filter) return true;
    const search = filter.toLowerCase();

    // Search in Target (Mobile), Template ID, or Operation Type
    return (
      (l.target && l.target.toLowerCase().includes(search)) ||
      (l.templateId && l.templateId.includes(search)) ||
      (l.operationType && l.operationType.toLowerCase().includes(search))
    );
  });

  // Split logs into categories for Tabs
  const bulkLogs = globalFiltered.filter(l => l.operationType.toLowerCase().includes("bulk"));
  const groupLogs = globalFiltered.filter(l => l.operationType.toLowerCase().includes("group"));
  const singleLogs = globalFiltered.filter(l => {
    const type = l.operationType.toLowerCase();
    return !type.includes("bulk") && !type.includes("group");
  });

  // ============================================================================
  // 5. DATA PARSING & HELPERS
  // ============================================================================

  /**
   * Parses the complex nested JSON response stored in the database.
   * Handles legacy formats and API variations.
   */
  const parseLogData = (logEntry) => {
    if (!logEntry) return {};

    try {
      // 1. Unpack outer 'response'
      const dbResponse = typeof logEntry.response === "string"
        ? JSON.parse(logEntry.response)
        : logEntry.response;

      // 2. Unpack 'api_response'
      let apiResponse = dbResponse?.api_response
        ? (typeof dbResponse.api_response === "string" ? JSON.parse(dbResponse.api_response) : dbResponse.api_response)
        : dbResponse;

      // 3. Handle double-nested edge cases
      if (apiResponse?.api_response) {
        apiResponse = typeof apiResponse.api_response === "string"
          ? JSON.parse(apiResponse.api_response)
          : apiResponse.api_response;
      }

      // 4. Unpack 'request_payload' (contains Scheduled Time)
      const requestPayload = dbResponse?.request_payload
        ? (typeof dbResponse.request_payload === "string" ? JSON.parse(dbResponse.request_payload) : dbResponse.request_payload)
        : {};

      const isGroup = logEntry.operationType?.toLowerCase().includes("group") ||
        logEntry.operationType?.toLowerCase().includes("bulk");

      // --- CASE A: GROUP OR BULK ---
      if (isGroup) {
        return {
          isGroup: true,
          batchId: apiResponse?.BatchId || apiResponse?.Batch_Id || null,
          batchSummary: apiResponse?.batch_summary || "Batch Processed",
          individualLogs: apiResponse?.individual_logs || [],
          bookingTime: logEntry.timestamp,
          scheduledTime: requestPayload?.Scheduled_Time || null,
          rawResponse: apiResponse,
        };
      }

      // --- CASE B: SINGLE SMS ---
      let messageContent = "Template ID Only";
      if (requestPayload?.Template_Keys_and_Values) {
        messageContent = requestPayload.Template_Keys_and_Values.map(kv => `${kv.Key}: ${kv.Value}`).join(", ");
      } else if (requestPayload?.filename) {
        messageContent = `File: ${requestPayload.filename}`;
      }

      return {
        isGroup: false,
        messageId: apiResponse?.Message_Id || apiResponse?.MessageId || null,
        batchId: apiResponse?.Batch_Id || apiResponse?.BatchId || null,
        scheduledTime: requestPayload?.Scheduled_Time || null,
        bookingTime: logEntry.timestamp,
        messageContent,
        rawRequest: requestPayload,
        rawResponse: apiResponse,
      };
    } catch (e) {
      return {
        isGroup: false,
        error: "Parse Error: " + e.message,
        raw: logEntry.response
      };
    }
  };

  /**
   * Safe Date Parser for BSNL format (DD-MM-YYYY HH:mm:ss).
   * Standard Date() often fails with DD-MM-YYYY.
   */
  const parseBsnlDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const [datePart, timePart] = dateStr.split(' ');
      const [day, month, year] = datePart.split('-');
      // Convert to ISO: YYYY-MM-DDTHH:mm:ss
      return new Date(`${year}-${month}-${day}T${timePart}`);
    } catch (e) {
      console.error("Date parse error", e);
      return null;
    }
  };

  /**
   * Determines if a message allows cancellation based on time.
   */
  const getCancellationStatus = (scheduledTimeStr) => {
    if (!scheduledTimeStr) return { canCancel: false, reason: "Not scheduled" };

    const scheduledDate = parseBsnlDate(scheduledTimeStr);

    if (!scheduledDate || isNaN(scheduledDate.getTime())) {
      return { canCancel: false, reason: "Invalid date format" };
    }

    const now = new Date();
    const safeCancelThreshold = new Date(now.getTime() + CANCELLATION_BUFFER_MINUTES * 60000);

    // Rule 1: Cannot cancel past events
    if (scheduledDate <= now) {
      return { canCancel: false, reason: "Time has passed" };
    }

    // Rule 2: Cannot cancel within buffer window (30 mins)
    if (scheduledDate <= safeCancelThreshold) {
      return { canCancel: false, reason: `Too close to delivery (within ${CANCELLATION_BUFFER_MINUTES} mins)` };
    }

    return { canCancel: true, reason: "" };
  };

  // 🔥 Determines Group Batch Final Status (Scheduled / Partial / Fully Cancelled)
  const computeBatchStatus = (rows) => {
    if (!rows || rows.length === 0) return "Scheduled";

    const total = rows.length;
    const cancelled = rows.filter(r => r.status === "Cancelled").length;

    if (cancelled === 0) return "Scheduled";
    if (cancelled === total) return "Cancelled";
    return "Partially Cancelled";
  };


  // ============================================================================
  // 6. ACTION HANDLERS
  // ============================================================================

  /**
   * Opens the Modal and fetches details/status for a log entry.
   */
  const handleTrackStatus = async (logEntry) => {
    const parsed = parseLogData(logEntry);
    setFetchedBatchDetails([]);
    setCancelAuditLog(null); // 🔥 Reset Audit Log state on open

    // Scenario 1: Group Batch (Fetch details from SQL)
    if (parsed.isGroup && parsed.batchId) {
      setTrackerLoading(true);
      setSelectedLog(logEntry);

      try {
        const res = await axios.get(`${CONFIG.DB_BASE}/batch-details/${parsed.batchId}`);

        // 🔥 ROBUST MAPPING: Handles both PascalCase (C#) and camelCase (JSON)
        const mappedData = res.data.map(item => {

          // 1. Detect Cancellation Truth (Check both cases)
          const isCancelledBool = Boolean(item.isCancelled || item.IsCancelled);

          // 2. Determine Status String
          const derivedStatus = isCancelledBool ? "Cancelled" : (item.status || item.Status);

          return {
            mobile: item.mobile || item.Mobile,
            status: derivedStatus,
            messageId: item.messageId || item.MessageId,
            sentTime: item.sentTime || item.SentTime,

            // 3. Store Boolean for UI Logic
            isCancelled: isCancelledBool,

            cancelledAt: item.cancelledAt || item.CancelledAt
          };
        });

        setFetchedBatchDetails(mappedData);

        // Update Cache
        setBatchCache(prev => ({ ...prev, [parsed.batchId]: mappedData }));

        // Update Main Table Status
        applyBatchStatusToLogs(parsed.batchId, mappedData);

      } catch (e) {
        console.error("Fetch Error", e);
        notify("Failed to fetch batch details", "danger");
      } finally {
        setTrackerLoading(false);
      }
    }

    // ... (Keep existing Scenario 2, 3, 4 logic unchanged) ...
    // Scenario 2: Legacy Group
    else if (parsed.isGroup) {
      setSelectedLog(logEntry);
      return;
    }
    // Scenario 3: Missing IDs
    else if (!parsed.messageId && !parsed.batchId) {
      notify("Tracking Unavailable: No ID found.", "warning");
      setSelectedLog(logEntry);
      return;
    }
    // Scenario 4: Single SMS
    else {
      setTrackerLoading(true);
      setTrackerData(null);
      setSelectedLog(logEntry);
      try {
        let res;
        if (parsed.batchId) {
          if (addLog) addLog(`Tracking Batch ID: ${parsed.batchId}...`);
          res = await axios.post(`${CONFIG.API_BASE}/check-bulk-status`, { Batch_Id: parsed.batchId });
        } else {
          if (addLog) addLog(`Tracking Message ID: ${parsed.messageId}...`);
          res = await axios.post(`${CONFIG.API_BASE}/check-status`, { Message_id: parsed.messageId });
        }
        setTrackerData(res.data);
      } catch (e) {
        setTrackerData({ Error: "Failed to fetch status: " + e.message });
      } finally {
        setTrackerLoading(false);
      }
    }
  };

  /**
   * Tracks a specific sub-message inside a group batch.
   * 🔥 Updated to handle fetching Audit Logs for cancelled messages.
   */
  const handleSubTrack = async (msgId, isCancelled) => {
    if (!msgId) { alert("No Message ID available."); return; }

    setTrackerLoading(true);
    setTrackerData(null);
    setCancelAuditLog(null);

    // 🔥 NEW: If message is already cancelled, fetch Audit Log instead of BSNL Status
    if (isCancelled) {
      try {
        const auditRes = await axios.get(`${CONFIG.DB_BASE}/cancellation-info/${msgId}`);
        setCancelAuditLog(auditRes.data);
        setTrackerLoading(false);
        return; // Stop here, don't call BSNL
      } catch (e) {
        console.log("No audit log found locally, attempting live status check...", e);
        // Fallthrough to live check if local audit fetch fails (just in case)
      }
    }

    try {
      const res = await axios.post(`${CONFIG.API_BASE}/check-status`, {
        Message_id: msgId
      });

      setTrackerData(res.data);

      // 🔥 AUTO SYNC CANCELLED STATUS TO SQLITE
      if (
        res.data?.Message_Status_Description?.toLowerCase()?.includes("cancel") ||
        res.data?.Message_Status === "Cancelled" ||
        res.data?.Message_Status === "Canceled"
      ) {
        await axios.post(`${CONFIG.API_BASE}/sync-cancel-status`, {
          MessageId: msgId,
          Response: JSON.stringify(res.data)
        });

        // 🔥 Refresh batch details
        const batchId = parseLogData(selectedLog).batchId;
        const refreshed = await axios.get(`${CONFIG.DB_BASE}/batch-details/${batchId}`);

        // Re-map with robust logic
        const mapped = refreshed.data.map(item => {
          const isCancelledBool = Boolean(item.isCancelled || item.IsCancelled);
          return {
            mobile: item.mobile || item.Mobile,
            status: isCancelledBool ? "Cancelled" : (item.status || item.Status),
            messageId: item.messageId || item.MessageId,
            sentTime: item.sentTime || item.SentTime,
            isCancelled: isCancelledBool
          };
        });

        setFetchedBatchDetails(mapped);
        setBatchCache(prev => ({ ...prev, [batchId]: mapped })); // Update cache
        applyBatchStatusToLogs(batchId, mapped);
      }

    } catch (e) {
      setTrackerData({ Error: e.message });
    } finally {
      setTrackerLoading(false);
    }
  };


  // 🔵 NEW: Fetch Cancellation Info (DB Only)
  // 🔵 Fetch Cancellation Info + Sync UI State
  const handleInfo = async (msgId) => {
    if (!msgId) return;

    setTrackerLoading(true);
    setTrackerData(null);
    setCancelAuditLog(null);

    try {
      const res = await axios.get(`${CONFIG.DB_BASE}/cancellation-info/${msgId}`);
      const audit = res.data;

      // 1️⃣ Show audit log in modal
      setCancelAuditLog(audit);

      // 2️⃣ If audit confirms cancellation → sync table UI
      if (
        (audit.Status || audit.status)?.toLowerCase() === "cancelled"
      ) {
        const batchId = parseLogData(selectedLog)?.batchId;

        // 🔁 Update fetched batch details
        setFetchedBatchDetails(prev =>
          prev.map(r =>
            r.messageId === msgId
              ? {
                ...r,
                status: "Cancelled",
                isCancelled: true,
                cancelledAt: audit.CancelledAt || audit.cancelledAt
              }
              : r
          )
        );

        // 🔁 Update cache (VERY IMPORTANT)
        if (batchId && batchCache[batchId]) {
          const updated = batchCache[batchId].map(r =>
            r.messageId === msgId
              ? {
                ...r,
                status: "Cancelled",
                isCancelled: true,
                cancelledAt: audit.CancelledAt || audit.cancelledAt
              }
              : r
          );

          setBatchCache(prev => ({ ...prev, [batchId]: updated }));
          applyBatchStatusToLogs(batchId, updated);
        }
      }

    } catch (e) {
      notify("No cancellation info found for this message.", "warning");
    } finally {
      setTrackerLoading(false);
    }
  };



  const applyBatchStatusToLogs = (batchId, updatedRows) => {
    const newBatchStatus = computeBatchStatus(updatedRows);

    setLogs(prevLogs =>
      prevLogs.map(l => {
        const p = parseLogData(l);
        if (p.isGroup && p.batchId === batchId) {
          return { ...l, status: newBatchStatus };
        }
        return l;
      })
    );
  };


  const handleGroupCancel = async (msgId) => {
    if (!msgId) return;
    if (!window.confirm(`Cancel scheduled SMS ${msgId}?`)) return;

    setCancellingIds(prev => [...prev, msgId]);

    try {
      // 1️⃣ Single Call to Backend (Now handles DB Update too)
      const res = await axios.post(`${CONFIG.API_BASE}/cancel-schedule`, {
        Message_Id: msgId
      });

      // Check for success property in various formats BSNL/API might return
      const isSuccess = res.data?.Success || res.data?.success || (typeof res.data === 'string' && res.data.includes("Success"));

      if (!isSuccess) {
        notify("Cancel failed: " + JSON.stringify(res.data), "danger");
        return;
      }

      notify("Message cancelled successfully", "success");

      // 2️⃣ Update Local State (UI) - No extra API call needed
      setFetchedBatchDetails(prevRows => {
        const updatedRows = prevRows.map(r => {
          if (r.messageId === msgId) {
            return {
              ...r,
              status: "Cancelled",
              isCancelled: true,
              cancelledAt: new Date().toISOString()
            };
          }
          return r;
        });

        // Update Cache and Recalculate Main Table Status
        const batchId = parseLogData(selectedLog).batchId;
        setBatchCache(prev => ({ ...prev, [batchId]: updatedRows }));
        applyBatchStatusToLogs(batchId, updatedRows);

        return updatedRows;
      });

    } catch (e) {
      notify("Cancel failed: " + e.message, "danger");
    } finally {
      setCancellingIds(prev => prev.filter(x => x !== msgId));
    }
  };


  /**
   * 🔥 NEW: Handle Cancel All Messages in Batch
   * Cancels all messages in the currently viewed batch that are "Scheduled".
   */
  const handleBatchCancelAll = async () => {
    // 1. Resolve current active log list based on cache or state
    const details = parseLogData(selectedLog);
    const batchId = details.batchId;
    const currentLogs = fetchedBatchDetails.length > 0
      ? fetchedBatchDetails
      : batchCache[batchId] && batchCache[batchId].length > 0
        ? batchCache[batchId]
        : details.individualLogs || [];

    // 2. Filter for Scheduled items that have a Message ID
    const scheduledMsgs = currentLogs.filter(l => l.status === "Scheduled" && l.messageId);

    if (scheduledMsgs.length === 0) {
      notify("No scheduled messages found to cancel.", "info");
      return;
    }

    // 3. Confirm Action
    if (!window.confirm(`⚠️ Are you sure you want to cancel ALL ${scheduledMsgs.length} scheduled messages in this batch?`)) return;

    setCancelLoading(true);
    const idsToCancel = scheduledMsgs.map(m => m.messageId);
    setCancellingIds(idsToCancel); // Lock UI

    try {
      // 4. Concurrently execute cancel requests
      const promises = idsToCancel.map(id =>
        axios.post(`${CONFIG.API_BASE}/cancel-schedule`, { Message_Id: id })
          .then(res => ({
            id,
            success: res.data?.Success || res.data?.success || (typeof res.data === 'string' && res.data.includes("Success")),
            error: null
          }))
          .catch(err => ({ id, success: false, error: err.message }))
      );

      const results = await Promise.all(promises);
      const successfulIds = results.filter(r => r.success).map(r => r.id);

      // 5. Update UI with results
      setFetchedBatchDetails(prevRows => {
        const updatedRows = prevRows.map(r => {
          if (successfulIds.includes(r.messageId)) {
            return {
              ...r,
              status: "Cancelled",
              isCancelled: true,
              cancelledAt: new Date().toISOString()
            };
          }
          return r;
        });

        // Sync to Main Table & Cache
        setBatchCache(prev => ({ ...prev, [batchId]: updatedRows }));
        applyBatchStatusToLogs(batchId, updatedRows);

        return updatedRows;
      });

      // 6. Notify User
      const failedCount = results.length - successfulIds.length;
      if (failedCount > 0) {
        notify(`Partial Success: ${successfulIds.length} cancelled, ${failedCount} failed.`, "warning");
      } else {
        notify(`Successfully cancelled all ${successfulIds.length} messages.`, "success");
      }

    } catch (e) {
      notify("Batch Cancel Error: " + e.message, "danger");
    } finally {
      setCancelLoading(false);
      setCancellingIds([]);
    }
  };


  /**
   * [CRITICAL] Handles the Cancellation Process.
   * 1. Confirms with user.
   * 2. Calls Backend API to Cancel.
   * 3. Backend updates SQL Status.
   * 4. Frontend updates Local State (UI).
   */
  const handleCancelSchedule = async () => {
    if (!selectedLog) return;
    const { messageId } = parseLogData(selectedLog);

    if (!messageId) {
      notify("Error: Message ID not found.", "danger");
      return;
    }

    if (!window.confirm(`Cancel Schedule ID: ${messageId}?`)) return;

    setCancelLoading(true);
    try {
      // 1️⃣ Call Backend
      const res = await axios.post(`${CONFIG.API_BASE}/cancel-schedule`, { Message_Id: messageId });

      // [FIX HERE] Check for both 'Success' AND 'success'
      const isSuccess = res.data?.Success || res.data?.success || (typeof res.data === 'string' && res.data.includes("Success"));

      if (isSuccess) {
        notify("Schedule Cancelled", "success");

        // 2️⃣ Update Local UI State
        const updatedStatus = "Cancelled";

        // Update Main List
        setLogs(prevLogs => prevLogs.map(l => {
          const p = parseLogData(l);
          if (p.messageId === messageId) {
            return { ...l, status: updatedStatus };
          }
          return l;
        }));

        // Update Modal
        setSelectedLog(prev => ({ ...prev, status: updatedStatus }));

        // Update Tracker UI if visible
        setTrackerData({
          Message_Status_Description: "Schedule Cancelled by User",
          Success: true
        });

      } else {
        notify("Cancellation Failed: " + (res.data?.Error || "Unknown Error"), "danger");
      }
    } catch (e) {
      notify("Network Error: " + e.message, "danger");
    } finally {
      setCancelLoading(false);
    }
  };

  // ============================================================================
  // 7. RENDER HELPERS (JSX)
  // ============================================================================

  // Helper to render the main data tables
  const renderTable = (data, isBulk = false) => (
    <div className="border rounded shadow-sm overflow-hidden bg-white mt-3">
      <div className="table-responsive" style={{ maxHeight: "600px" }}>
        <Table size="sm" striped hover className="mb-0 align-middle">
          <thead className="table-dark sticky-top">
            <tr>
              <th className="ps-3 py-2">Booking Time</th>
              <th>Type</th>
              <th>{isBulk ? "Batch ID" : "Message ID"}</th>
              {isBulk && <th>Scheduled Time</th>}
              <th>Target / Info</th>
              <th>Template ID</th>
              <th className="text-center">Status</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center p-4">Loading...</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={8} className="text-center p-4 text-muted">No records found.</td></tr>
            ) : (
              data.map(row => {
                const parsed = parseLogData(row);
                const { scheduledTime, isGroup, messageId, batchId } = parsed;
                const displayId = messageId || batchId || "N/A";

                // 🔥 FIX: Compute group status from child messages
                const batchRows =
                  parsed.isGroup && parsed.batchId
                    ? batchCache[parsed.batchId] ||
                    fetchedBatchDetails ||
                    parsed.individualLogs ||
                    []
                    : [];


                const groupStatus = parsed.isGroup
                  ? computeBatchStatus(batchRows.length ? batchRows : parsed.individualLogs)
                  : row.status;


                return (
                  <tr key={row.id}>

                    <td className="ps-3 text-nowrap" style={{ fontSize: "0.85rem" }}>
                      {new Date(row.timestamp).toLocaleString()}
                    </td>
                    <td>
                      <Badge
                        bg={row.operationType.toLowerCase().includes('scheduled') ? 'warning' : (isGroup ? 'info' : 'light')}
                        text={isGroup ? 'white' : 'dark'}
                        className="border"
                      >
                        {row.operationType}
                      </Badge>
                    </td>
                    <td className="font-monospace text-primary small">
                      {displayId !== "N/A" ? displayId : <span className="text-muted">-</span>}
                    </td>

                    {isBulk && (
                      <td className="text-muted small">
                        {scheduledTime ? <span className="text-primary fw-bold">{scheduledTime}</span> : "Instant"}
                      </td>
                    )}

                    <td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.target}>
                      {row.target}
                    </td>
                    <td><span className="font-monospace text-muted small">{row.templateId}</span></td>

                    <td className="text-center">
                      {/* DYNAMIC STATUS BADGE */}
                      <Badge
                        bg={
                          groupStatus === "Cancelled" ? "secondary" :
                            groupStatus === "Partially Cancelled" ? "warning" :
                              groupStatus === "Scheduled" ? "info" :
                                "success"
                        }
                        pill
                      >
                        {groupStatus}
                      </Badge>

                    </td>

                    <td className="text-center">
                      <Button variant="link" size="sm" className="p-0 me-2" onClick={() => handleTrackStatus(row)}>View</Button>
                      <Button variant="outline-dark" size="sm" style={{ fontSize: "0.6rem" }} onClick={() => handleTrackStatus(row)}>
                        <i className={`bi ${isGroup ? 'bi-list-ul' : 'bi-geo-alt-fill'} me-1`}></i>
                        {isGroup ? "Details" : "Track"}
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );

  // Derived state for Modal Logic
  const details = selectedLog ? parseLogData(selectedLog) : {};
  const batchId = details.batchId;

  const displayLogs =
    fetchedBatchDetails.length > 0
      ? fetchedBatchDetails
      : batchCache[batchId] && batchCache[batchId].length > 0
        ? batchCache[batchId]
        : details.individualLogs || [];


  // Calculate Cancellation Eligibility for UI
  const isScheduled = selectedLog && selectedLog.operationType.toLowerCase().includes('scheduled');
  const isAlreadyCancelled = selectedLog && selectedLog.status === "Cancelled";

  const { canCancel, reason: cancelReason } = isScheduled
    ? getCancellationStatus(details.scheduledTime)
    : { canCancel: false, reason: "" };

  return (
    <div className="fade-in">

      {/* --- HEADER --- */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-bold text-primary m-0">Operation Logs</h4>
          <small className="text-muted">History of all transactions</small>
        </div>
        <div className="d-flex gap-2">
          <InputGroup size="sm" style={{ width: "250px" }}>
            <InputGroup.Text className="bg-white"><i className="bi bi-search"></i></InputGroup.Text>
            <Form.Control placeholder="Search history..." value={filter} onChange={e => setFilter(e.target.value)} />
          </InputGroup>
          <Button variant="outline-secondary" size="sm" onClick={loadHistory}><i className="bi bi-arrow-clockwise"></i></Button>
        </div>
      </div>

      {/* --- TABS --- */}
      <Tabs defaultActiveKey="single" id="history-tabs" className="mb-3">
        <Tab eventKey="single" title={`Single SMS (${singleLogs.length})`}>
          {renderTable(singleLogs, false)}
        </Tab>
        <Tab eventKey="group" title={`Group Batches (${groupLogs.length})`}>
          {renderTable(groupLogs, false)}
        </Tab>
        <Tab eventKey="bulk" title={`Bulk Uploads (${bulkLogs.length})`}>
          {renderTable(bulkLogs, true)}
        </Tab>
      </Tabs>

      {/* --- DETAILS MODAL --- */}
      <Modal show={!!selectedLog} onHide={() => setSelectedLog(null)} size="lg" centered scrollable>
        <Modal.Header closeButton className="bg-light">
          <div className="w-100 d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-bold text-primary" style={{ fontSize: "0.95rem" }}>
                {details.isGroup ? "Group Batch Details" : "Transaction Details"}
              </div>
              <div className="small text-muted mt-1">
                ID: <span className="font-monospace fw-bold text-dark">{details.messageId || details.batchId || "N/A"}</span>
              </div>
            </div>
            {trackerData && (
              <Badge bg={trackerData.Success ? "success" : "info"} pill className="px-3 py-2">
                {trackerData.Success ? "Request Successful" : "Live Status"}
              </Badge>
            )}
          </div>
        </Modal.Header>

        <Modal.Body>
          {trackerLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Fetching status...</p>
            </div>
          ) : cancelAuditLog ? (

            /* 🔥 NEW: AUDIT LOG VIEW FOR CANCELLED MESSAGES */
            <Alert variant="secondary" className="border-0 shadow-sm">
              <Alert.Heading className="fs-6 fw-bold"><i className="bi bi-shield-lock-fill me-2"></i>Cancellation Audit Log</Alert.Heading>
              <hr />
              <div className="small">
                <div><strong>Message ID:</strong> {cancelAuditLog.MessageId || cancelAuditLog.messageId}</div>
                <div><strong>Cancelled At:</strong> {new Date(cancelAuditLog.CancelledAt || cancelAuditLog.cancelledAt).toLocaleString()}</div>
                <div><strong>Status:</strong> {cancelAuditLog.Status || cancelAuditLog.status}</div>
                <div><strong>Response:</strong> <pre className="mt-1 bg-light p-2 border rounded">{cancelAuditLog.ResponseJson || cancelAuditLog.responseJson}</pre></div>
                <div className="mt-2 text-muted fst-italic">This data was fetched from the secure audit log table.</div>
              </div>
              <div className="text-center mt-3">
                <Button variant="outline-dark" size="sm" onClick={() => setCancelAuditLog(null)}>Back</Button>
              </div>
            </Alert>

          ) : trackerData ? (

            /* --- LIVE TRACKER VIEW --- */
            <div className="animate-fade-in">
              <div className={`card border-0 shadow-sm mb-3 text-white ${trackerData.Message_Status === "7" || trackerData.Success ? "bg-success" : (trackerData.Error ? "bg-danger" : "bg-primary")}`}>
                <div className="card-body text-center py-3">
                  <h5 className="fw-bold mb-1">
                    {trackerData.Success || trackerData.Message_Status_Description || trackerData.Error || "Status Retrieved"}
                  </h5>
                  {trackerData.Message_Status && (
                    <Badge bg="light" text="dark" className="fs-6 px-3 mt-2">
                      Status Code: {trackerData.Message_Status}
                    </Badge>
                  )}
                </div>
              </div>

              {!trackerData.Success && trackerData.Create_Time && (
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Card className="h-100 border-0 shadow-sm bg-light">
                      <Card.Body className="small">
                        <div className="fw-bold text-muted border-bottom pb-2 mb-2">TIMESTAMPS</div>
                        <div className="d-flex justify-content-between mb-1"><span>Created:</span> <strong>{trackerData.Create_Time}</strong></div>
                        <div className="d-flex justify-content-between mb-1"><span>Carrier Accepted:</span> <strong>{trackerData.Carrier_Accept_Time}</strong></div>
                        <div className="d-flex justify-content-between text-success"><span>Delivered:</span> <strong>{trackerData.Delivery_Success_Time || "Pending"}</strong></div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="h-100 border-0 shadow-sm bg-light">
                      <Card.Body className="small">
                        <div className="fw-bold text-muted border-bottom pb-2 mb-2">METADATA</div>
                        <div className="d-flex justify-content-between mb-1"><span>Receiver:</span> <strong>{trackerData.Receiver}</strong></div>
                        <div className="d-flex justify-content-between mb-1"><span>SMS Count:</span> <strong>{trackerData.SMS_Count}</strong></div>
                        <div className="d-flex justify-content-between text-danger"><span>DLR Error:</span> <strong>{trackerData.DLR_Error_Code || "None"}</strong></div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {!trackerData.Message_Status_Description && !trackerData.Error && !trackerData.Success &&
                <pre className="bg-light p-3 border rounded small">{JSON.stringify(trackerData, null, 2)}</pre>
              }

              <div className="text-center mt-3">
                <Button variant="outline-dark" size="sm" onClick={() => setTrackerData(null)}>
                  <i className="bi bi-arrow-left me-1"></i>Back to List
                </Button>
              </div>
            </div>

          ) : selectedLog && details.isGroup ? (

            /* --- GROUP BATCH VIEW --- */
            <>
              {/* 🔥 NEW: Batch Stats & Actions */}
              <div className="d-flex justify-content-between align-items-end mb-3">
                <div className="w-100">
                  <div className="alert alert-light border shadow-sm py-2 px-3 mb-3 d-flex justify-content-between align-items-center">
                    <div className="small">
                      <strong>Batch Summary:</strong> {details.batchSummary} <br />
                      <span className="text-muted">Booking Time: {new Date(details.bookingTime).toLocaleString()}</span>
                    </div>
                  </div>

                  {/* STATS ROW */}
                  <Row className="g-2 text-center">
                    <Col>
                      <Card className="border-0 shadow-sm bg-light h-100">
                        <Card.Body className="p-2">
                          <h6 className="text-muted small mb-0">Total</h6>
                          <div className="fw-bold fs-5">{displayLogs.length}</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="border-0 shadow-sm bg-success text-white h-100">
                        <Card.Body className="p-2">
                          <h6 className="text-white-50 small mb-0">Sent</h6>
                          <div className="fw-bold fs-5">
                            {displayLogs.filter(l => ["Success", "Sent", "Submitted"].includes(l.status)).length}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="border-0 shadow-sm bg-secondary text-white h-100">
                        <Card.Body className="p-2">
                          <h6 className="text-white-50 small mb-0">Cancelled</h6>
                          <div className="fw-bold fs-5">
                            {displayLogs.filter(l => l.status === "Cancelled" || l.isCancelled).length}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col>
                      <Card className="border-0 shadow-sm bg-danger text-white h-100">
                        <Card.Body className="p-2">
                          <h6 className="text-white-50 small mb-0">Failed</h6>
                          <div className="fw-bold fs-5">
                            {displayLogs.filter(l => !["Success", "Sent", "Submitted", "Scheduled", "Cancelled"].includes(l.status)).length}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              </div>

              {/* 🔥 NEW: Cancel All Button */}
              {displayLogs.some(l => l.status === "Scheduled") && (
                <div className="d-flex justify-content-end mb-2">
                  <Button
                    variant="danger"
                    size="sm"
                    className="fw-bold"
                    onClick={handleBatchCancelAll}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? (
                      <><Spinner size="sm" animation="border" className="me-1" /> Cancelling...</>
                    ) : (
                      <><i className="bi bi-slash-circle me-2"></i>Cancel All Scheduled</>
                    )}
                  </Button>
                </div>
              )}

              <div className="table-responsive">
                <Table size="sm" striped hover className="small mb-0 align-middle">
                  <thead className="table-light sticky-top">
                    <tr><th style={{ width: '30px' }}>#</th>
                      <th>Mobile</th>
                      <th>Sent Time</th>
                      <th>Status</th>
                      <th>Cancel State</th>
                      <th>Message ID</th>
                      <th className="text-end">Action</th></tr>
                  </thead>
                  <tbody>
                    {displayLogs.length === 0 ? <tr><td colSpan="6" className="text-center p-3 text-muted">No details available.</td></tr> :
                      displayLogs.map((log, i) => (
                        <tr key={i}>
                          <td className="text-muted">{i + 1}</td>

                          <td className="fw-bold">{log.mobile}</td>

                       <td className="text-muted text-nowrap">
  {log.sentTime
    ? new Date(log.sentTime).toLocaleString()
    : "-"}
</td>


                          {/* STATUS */}
                          <td>
                            <Badge
                              bg={
                                log.status === "Success" || log.status === "Sent" || log.status === "Submitted"
                                  ? "success"
                                  : log.status === "Scheduled"
                                    ? "warning"
                                    : log.status === "Cancelled"
                                      ? "secondary"
                                      : "danger"
                              }
                            >
                              {log.status}
                            </Badge>
                          </td>

                          {/* CANCEL STATE */}
                          <td>
                            {log.isCancelled ? (
                              <Badge bg="secondary">
                                <i className="bi bi-slash-circle me-1"></i>Cancelled
                              </Badge>
                            ) : (
                              <Badge bg="success">
                                <i className="bi bi-check-circle me-1"></i>Active
                              </Badge>
                            )}
                          </td>

                          {/* MESSAGE ID */}
                          <td className="font-monospace text-muted" style={{ fontSize: "0.8rem" }}>
                            {log.messageId || "N/A"}
                          </td>

                          {/* ACTION */}
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2 align-items-center">

                              {log.messageId && (
                                <OverlayTrigger placement="top" overlay={<Tooltip>Cancellation Info</Tooltip>}>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                                    onClick={() => handleInfo(log.messageId)}
                                  >
                                    <i className="bi bi-info-circle"></i>
                                  </Button>
                                </OverlayTrigger>
                              )}


                              {/* 🔥 NEW: Check/Refresh Status Button */}
                              {log.messageId && (
                                <OverlayTrigger placement="top" overlay={<Tooltip>Check Status / Audit Log</Tooltip>}>
                                  <Button
                                    size="sm"
                                    variant="light"
                                    className="border"
                                    style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                                    onClick={() => handleSubTrack(log.messageId, log.isCancelled)}
                                  >
                                    <i className={`bi ${log.isCancelled ? "bi-eye-fill text-dark" : "bi-arrow-repeat text-primary"}`}></i>
                                  </Button>
                                </OverlayTrigger>
                              )}

                              {/* EXISTING LOGIC */}
                              {log.status === "Scheduled" && log.messageId ? (
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                                  disabled={cancellingIds.includes(log.messageId) || cancelLoading}
                                  onClick={() => handleGroupCancel(log.messageId)}
                                >
                                  {cancellingIds.includes(log.messageId) ? "..." : "Cancel"}
                                </Button>
                              ) : log.status === "Cancelled" ? (
                                <Badge bg="secondary">Cancelled</Badge>
                              ) : log.messageId ? (
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                                  onClick={() => handleSubTrack(log.messageId, false)}
                                >
                                  Track
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>

                      ))}
                  </tbody>
                </Table>
              </div>
            </>

          ) : selectedLog && (

            /* --- SINGLE MSG VIEW (Standard) --- */
            <>
              <Alert variant="light" className="border shadow-sm mb-3">
                <div className="row g-2 small">
                  <div className="col-6"><strong><i className="bi bi-calendar-check me-2"></i>Booking Time:</strong> <br />{new Date(details.bookingTime).toLocaleString()}</div>
                  <div className="col-6"><strong><i className="bi bi-clock-history me-2"></i>Scheduled:</strong> <br />{details.scheduledTime || "Immediate"}</div>

                  <div className="col-12 mt-3">
                    <strong><i className="bi bi-hash me-2"></i>{details.batchId ? "Batch ID" : "Message ID"}:</strong>
                    <span className="font-monospace bg-white border rounded px-2 ms-2 text-primary fw-bold">
                      {details.messageId || details.batchId || "N/A"}
                    </span>
                  </div>

                  {/* DYNAMIC MODAL STATUS */}
                  <div className="col-6 mt-3"><strong><i className="bi bi-activity me-2"></i>Status:</strong> <br />
                    <Badge bg={selectedLog.status === 'Success' ? 'success' : selectedLog.status === 'Cancelled' ? 'secondary' : 'danger'}>
                      {selectedLog.status}
                    </Badge>
                  </div>
                </div>
              </Alert>

              <Card className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white fw-bold small text-muted">MESSAGE CONTENT</Card.Header>
                <Card.Body className="bg-light text-dark small font-monospace">{details.messageContent}</Card.Body>
              </Card>

              {/* CANCELLATION LOGIC */}
              {isScheduled && !isAlreadyCancelled && (
                <div className="text-center mb-3">
                  {canCancel ? (
                    <Button variant="danger" size="sm" onClick={handleCancelSchedule} disabled={cancelLoading}>
                      {cancelLoading ? <Spinner size="sm" animation="border" /> : <><i className="bi bi-x-circle me-2"></i>CANCEL SCHEDULE</>}
                    </Button>
                  ) : (
                    <OverlayTrigger placement="top" overlay={<Tooltip>{cancelReason}</Tooltip>}>
                      <span className="d-inline-block">
                        <Button variant="secondary" size="sm" disabled>
                          <i className="bi bi-x-circle me-2"></i>CANCEL SCHEDULE
                        </Button>
                      </span>
                    </OverlayTrigger>
                  )}
                  {!canCancel && <div className="text-muted mt-2 small fst-italic"><i className="bi bi-info-circle me-1"></i>{cancelReason}</div>}
                </div>
              )}

              {/* ALREADY CANCELLED STATE */}
              {isAlreadyCancelled && (
                <div className="text-center mb-3">
                  <Button variant="secondary" size="sm" disabled>
                    <i className="bi bi-slash-circle me-2"></i>SCHEDULE CANCELLED
                  </Button>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 bg-light">
          <Button variant="secondary" size="sm" onClick={() => setSelectedLog(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}



export default HistoryManager;